import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import { Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { FormattedMessage } from "react-intl";

import type { IProject } from "@citrine/resources/project";
import { ProjectClient } from "@citrine/resources/project";
import type { Team } from "@citrine/resources/team";
import { wrapFormikErrors } from "@citrine/utils/validation";

import { CancelButton, Dialog, SubmitButton } from "#src/components/form";
import { useFindAllTeamsWithActions } from "#src/hooks/use-find-all-teams-with-actions";
import { useResourceNotifications } from "#src/hooks/use-resource-notifications";
import { useValidation } from "#src/hooks/use-validation";

import { NewProjectDialogContent, createEmptyProject } from "./common";
import * as sx from "./new-project-card.sx";
import type { ProjectValues } from "./new-project-dialog";

interface NewProjectCardProps {
  onCreate: (project: IProject) => void;
}

function NewProjectDialog({
  onCreate,
  teams,
}: NewProjectCardProps & {
  teams: Team[];
}) {
  const [errorMessage, setErrorMessage] = useState<string>();
  const [initialValues, setInitialValues] = useState<ProjectValues>();
  const [teamId, setTeamId] = useState("");

  const onClose = useCallback(
    () => setInitialValues(undefined),
    [setInitialValues],
  );
  const onClick = useCallback(() => {
    setErrorMessage(undefined);
    setInitialValues({ ...createEmptyProject(), teamId: "" });
  }, []);

  const projectCreateNotifications = useResourceNotifications(
    "project",
    "create",
  );
  const { mutateAsync: createProject } = ProjectClient.getInstance(
    teamId,
  ).useCreate({
    ...projectCreateNotifications,
    onSuccess(newProject) {
      setErrorMessage(undefined);
      projectCreateNotifications.onSuccess();
      onCreate(newProject);
    },
  });

  const validator = useValidation();
  const validate = useCallback(
    (project: ProjectValues) =>
      wrapFormikErrors({
        name: validator.isRequired(project.name),
        teamId: validator.isRequired(project.teamId),
      }),
    [validator],
  );
  return (
    <>
      <sx.Card data-fullstoryid="projects.add-card" onClick={onClick}>
        <sx.Label>
          <AddCircleOutline
            fontSize="large"
            color="disabled"
            style={{
              fontSize: 64,
            }}
          />
          <Typography variant="h4" component="span">
            <FormattedMessage id="resources.project.create-card" />
          </Typography>
        </sx.Label>
      </sx.Card>
      <Dialog
        data-testid="new-project-dialog"
        title={<FormattedMessage id="resources.project.create" />}
        initialValues={initialValues}
        validate={validate}
        validateOnBlur={false}
        onSubmit={async (project) => {
          try {
            await createProject(project);
          } catch (e) {
            setErrorMessage(
              e.response?.data?.errors?.[0]?.msg ??
                e.response?.data?.message ??
                e.message,
            );
            // keep the dialog open
            return false;
          }
        }}
        onClose={onClose}
        actions={
          <>
            <CancelButton onClick={onClose} />
            <SubmitButton data-fullstoryid="projects.add-card.submit">
              <FormattedMessage id="projects.dialog.submit" />
            </SubmitButton>
          </>
        }
      >
        <NewProjectDialogContent
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          teams={teams}
          setTeamId={setTeamId}
        />
      </Dialog>
    </>
  );
}

export function NewProjectCard(props: NewProjectCardProps) {
  const membershipsQuery = useFindAllTeamsWithActions();

  const writableTeams = membershipsQuery.data?.filter((membership) =>
    membership.actions.includes("WRITE"),
  );

  return writableTeams?.length ? (
    <NewProjectDialog {...props} teams={writableTeams} />
  ) : null;
}
